import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { EditRowDetailsModal } from '@app/flex-framework/components/modals/edit-row-details-modal/edit-row-details-modal.component';
import { LinkRowModal } from '@app/flex-framework/components/modals/link-row-modal/link-row-modal.component';
import {
  FlexRow,
  FlexRowSummary,
  LinkTextRow,
  OptionalResourceRow,
} from '@app/flex-framework/flex-api.model';
import { LinkRowLink } from '@app/shared/components/link-row/link-row.model';
import { MenuViewModel } from '@app/shared/components/menu/menu.component';
import { TargetType } from '@app/shared/models/core-api.model';
import {
  ModalInputBindings,
  ModalService,
} from '@app/shared/services/modal.service';
import { TargetsService } from '@app/shared/services/targets.service';
import { TargetService } from '@app/target/services/target.service';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { FreeTextRowModal } from '../components/modals/free-text-row-modal/free-text-row-modal.component';
import { FlexRowService } from './flex-row.service';

@Injectable({
  providedIn: 'root',
})
export class FlexRowMenuService {
  private i18n = this.translate.instant([
    'dgFlexRow_EditDetails',
    'dgFlexRow_EditSectionDetails',
    'Core_ViewAll',
    'dgFlexRow_YesDeleteButton',
    'Core_Delete',
  ]);

  constructor(
    private modalService: ModalService,
    private targetService: TargetService,
    private targetsService: TargetsService,
    private translate: TranslateService,
    private flexRowService: FlexRowService
  ) {}

  public getFlexRowMenu(
    row: FlexRow,
    activatedRoute: ActivatedRoute,
    onEdit: (row: Partial<FlexRow> | void) => void,
    onRemove: () => void
  ): MenuViewModel[] {
    const targetResponse = this.targetService.targetResponse;

    // Assume flex row is used outside of a Target page (making it static so
    // it doesn't need a menu) and return an empty menu.
    if (!targetResponse) {
      return [];
    }

    const targetType = targetResponse.target.targetType;
    const isOpportunity = row.flexRowType === 'Opportunity';

    const menuConfig: MenuViewModel[] = [
      {
        id: 'edit',
        title: this.i18n.dgFlexRow_EditDetails,
        preventRefocus: true,
        defaultAction: () =>
          this.editRow(row, targetType).subscribe((updatedRow) => {
            // Don't attempt to update the entire row here; we don't
            // necessarily have the most recent version of the row resources
            // so we should let FlexRowComponent handle it.
            if (updatedRow) {
              return onEdit(updatedRow);
            }
            // If the value for resources is instead null, we're removing the row.
            return onRemove();
          }),
        isHidden: () => isOpportunity,
      },
    ];

    return menuConfig;
  }

  public getRowLinksConfig(
    row: FlexRow,
    isEditing: boolean,
    url?: string
  ): LinkRowLink[] {
    if (url.includes('plan') || url.includes('explore')) {
      const routerLink: LinkRowLink = {
        label: this.i18n.Core_ViewAll,
        routerLink: [
          'details',
          row.section.targetId, // parentId
          row.section.resourceType, // collectionType
          row.section.parentNode, // parentNode
        ],
      };
      if (isEditing) {
        routerLink.queryParams = { editMode: true };
      }
      return [routerLink];
    }
  }

  private editRow(
    row: FlexRow,
    targetType: TargetType
  ): Observable<Partial<FlexRow> | void> {
    const flexRowType = row.flexRowType || row.section.resourceType;
    if (flexRowType === 'LinkText' || flexRowType === 'FreeformText') {
      return this.editOptionalRow(row, targetType);
    } else {
      return this.editFlexRow(row, targetType);
    }
  }

  /**
   * This method, despite its name, ONLY updates display settings /
   * name / description, and not the target resources, so we should
   * not bother to return our resources for editing at this point.
   *
   * @param row
   * @param targetType
   */
  private editFlexRow(
    row: FlexRow,
    targetType: TargetType
  ): Observable<Partial<FlexRow> | void> {
    if (!row.section.name) {
      // If the name hasn't been set yet, add it here to pre-populate the field
      // https://degreedjira.atlassian.net/browse/PD-45059
      row.section.name = row.displayType;
    }

    const inputs: ModalInputBindings = {
      row,
      targetType,
      sectionDefaultTitle: row.sectionDefaultTitle,
    };
    const modalOptions = {
      windowClass: 'lg-modal',
      inputs,
    };

    return this.modalService
      .show<FlexRowSummary>(EditRowDetailsModal, modalOptions)
      .pipe(
        switchMap((section: FlexRowSummary) => {
          // If data is at this point undefined, then the modal was closed after
          // deleting the row, so we can jump ship here.
          if (!section) {
            return of(null);
          }

          // merge the existing section data that contains additional settings
          const updatedSection = {
            ...row.section,
            ...section,
          };

          return this.targetsService
            .updateTargetSection(updatedSection, targetType)
            .pipe(
              // then package the updates into a proper row and send that back
              map((section: FlexRowSummary) => {
                if (section) {
                  return { section } as FlexRow;
                }
              })
            );
        })
      );
  }

  private editOptionalRow(
    row: FlexRow,
    targetType: TargetType
  ): Observable<FlexRow | void> {
    let modal;
    const resourceType = row.section.resourceType;
    const inputs = {
      row,
      isEditing: true,
    };
    const modalOptions = {
      windowClass: 'lg-modal',
      inputs,
    };

    if (resourceType === 'LinkText') {
      modal = LinkRowModal;
    } else if (resourceType === 'FreeformText') {
      modal = FreeTextRowModal;
    }
    return this.modalService
      .show<OptionalResourceRow>(modal, modalOptions)
      .pipe(
        // Update the flex row section first
        switchMap((optionalRow: OptionalResourceRow) => {
          // If data is at this point undefined, then the modal was closed after
          // deleting the row, so we can jump ship here.
          if (!optionalRow) {
            return of(null);
          }

          const { section, resource } = optionalRow;

          const name = section.name || (resource as LinkTextRow)?.label;

          const resourceImageId = section.resourceImage?.resourceImageId;

          const updatedSection = {
            // merge the existing section with the updated one
            // to keep previous settings
            ...row.section,
            ...section,
            name,
          };

          return this.targetsService
            .updateTargetSection(
              updatedSection,
              targetType,
              resource,
              resourceImageId
            )
            .pipe(map(() => ({ section: updatedSection, resource })));
        }),
        // if the row is a Text & Image we need to re-fetch the section
        // because the image is tied to the section description rather than the option resource values
        // TODO: This is only needed for the plan simplification since the image is new
        switchMap((optionalRow: OptionalResourceRow) => {
          if (resourceType === 'FreeformText' && optionalRow) {
            const { section, resource } = optionalRow;
            return this.targetsService
              .getTargetRowSections(section.targetId, section.targetResourceId)
              .pipe(
                map((sections) => {
                  // We provide the resource id to the getTargetRowSections() call
                  // that will return us only one section.
                  // Map to the next expected switchmap in the chain
                  return { section: sections[0], resource };
                })
              );
          } else if (optionalRow) {
            // if we're a Link Text row, just pass on the row info to the next switch
            return of(optionalRow);
          }
          return of(null);
        }),
        // then we want to re-fetch the "resources",
        // in this case it contains the Link Text or Text & Image content
        switchMap((optionalRow: OptionalResourceRow) => {
          if (!optionalRow) {
            return of(null);
          }
          return this.flexRowService.getRowResources(row, true, true).pipe(
            // lastly we want to package the new/update data in a flexrow and
            // ship that off to the edit emit to handle the view updates
            map((resource) => {
              const updatedRow: FlexRow = {
                ...row,
                // merge the existing section with the updated one
                // to keep previous settings
                section: {
                  ...row.section,
                  ...optionalRow.section,
                },
                // ..and format the resource how the backend does
                targetResources: resource,
              };

              return updatedRow;
            })
          );
        })
      );
  }
}
